import type { RecoveryCoordinator } from './index.types';
import { MobXProviderContext } from 'mobx-react';
import { ConnectionManager } from 'Modules/softphone/services/ConnectionManager';
import {
  useSoftphoneCoordinatorStore,
  useSoftphoneStore,
} from 'Modules/softphone/store';
import { recoveryLogger } from 'Modules/softphone/utils/logger';
import { useCallback, useContext } from 'react';
import type { RootStoreProps } from 'Stores/RootStore.types';

/**
 * Hook that coordinates reconnection for the active softphone
 *
 * Detects which softphone (legacy or new) is currently active and returns
 * the appropriate reconnection handler. Prevents duplicate reconnection attempts.
 *
 * Recovery state is read directly from stores without polling:
 * - ConnectionManager.getRecoveryState() for new softphone
 * - config.recoveryState getter for legacy softphone (MobX observable)
 *
 * @returns RecoveryCoordinator object with reconnect function and state flags
 */
export function useRecoveryCoordinator(): RecoveryCoordinator {
  // Get person ID and config for coordinator store and legacy recovery
  const rootStore = useContext<RootStoreProps>(MobXProviderContext);
  const { person, config } = rootStore;
  const personId = person?.loggedInPersonId;

  // Check which softphone is active
  const coordinatorStore = useSoftphoneCoordinatorStore(personId);
  const isNewSoftphoneEnabled = coordinatorStore(
    (state) => state.isNewSoftphoneEnabled
  );

  // Create reconnection function
  const reconnect = useCallback(async () => {
    if (isNewSoftphoneEnabled) {
      recoveryLogger.info('Triggering NEW softphone reconnection with FRESH config fetch');
      // New softphone: Use ConnectionManager with fresh config fetch (matches legacy behavior)
      // Pass enableRetry: false to prevent automatic retries on manual click (matches legacy)
      const manager = ConnectionManager.getInstance();
      await manager.reconnectWithConfigRefetch({ enableRetry: false });
    } else {
      recoveryLogger.info('Triggering LEGACY softphone reconnection');
      // Legacy softphone: Use ConfigStore recovery
      await config.startConfigRecovery();
    }
  }, [isNewSoftphoneEnabled, config]);

  // Get isReconnecting from reactive stores so UI updates when recovery finishes.
  // For new softphone: Read from Zustand store (ConnectionManager syncs recovery state to store).
  // For legacy softphone: Read from MobX observable (reactive in observer components).
  const isRecoveringFromStore = useSoftphoneStore(
    (state) => state.isRecovering
  );
  const isReconnecting = isNewSoftphoneEnabled
    ? isRecoveringFromStore
    : config.recoveryState.isRecovering;

  // Determine if reconnection is possible (prevents duplicate attempts)
  // Note: No useMemo here to allow MobX reactivity for legacy softphone
  const canReconnect = !isReconnecting;

  return { reconnect, isReconnecting, canReconnect };
}
